import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../lib/api';
import { 
  Search, Filter, Download, RefreshCw, AlertTriangle, Check, 
  Clock, User, Shield, ChefHat, Settings, Calendar, CreditCard,
  FileText, Mail, Database, ChevronLeft, ChevronRight, X
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';

// Category icons mapping
const CATEGORY_ICONS = {
  auth: Shield,
  reservation: Calendar,
  payment: CreditCard,
  recipe: FileText,
  menu: FileText,
  season: Calendar,
  employee: ChefHat,
  sector: Settings,
  config: Settings,
  user: User,
  intervention: ChefHat,
  pricing: CreditCard,
  simulation: Database,
  email: Mail,
  system: Database
};

// Category labels
const CATEGORY_LABELS = {
  auth: "Authentification",
  reservation: "Réservations",
  payment: "Paiements",
  recipe: "Recettes",
  menu: "Menus",
  season: "Saisons",
  employee: "Employés",
  sector: "Secteurs",
  config: "Configuration",
  user: "Utilisateurs",
  intervention: "Interventions",
  pricing: "Tarification",
  simulation: "Simulation",
  email: "Emails",
  system: "Système"
};

// Action labels
const ACTION_LABELS = {
  create: "Création",
  update: "Modification",
  delete: "Suppression",
  archive: "Archivage",
  login: "Connexion",
  logout: "Déconnexion",
  payment: "Paiement",
  refund: "Remboursement",
  cancel: "Annulation",
  validate: "Validation",
  checkout: "Check-out",
  generate: "Génération",
  send: "Envoi",
  export: "Export",
  restore: "Restauration",
  reset: "Réinitialisation"
};

// Role labels
const ROLE_LABELS = {
  admin: "Administrateur",
  employee: "Employé",
  client: "Client",
  system: "Système"
};

export default function AuditLogViewer() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState({
    category: '',
    action: '',
    user_role: '',
    success: '',
    search: '',
    start_date: '',
    end_date: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: 50,
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, v]) => v !== '' && v !== null)
        )
      };
      
      // Convert success filter
      if (params.success === 'true') params.success = true;
      else if (params.success === 'false') params.success = false;
      else delete params.success;
      
      const response = await adminApi.getAuditLogs(params);
      setLogs(response.data.logs);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Erreur lors du chargement du journal');
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  const loadStats = useCallback(async () => {
    try {
      const response = await adminApi.getAuditLogsStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }, []);

  useEffect(() => {
    loadLogs();
    loadStats();
  }, [loadLogs, loadStats]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      action: '',
      user_role: '',
      success: '',
      search: '',
      start_date: '',
      end_date: ''
    });
    setPage(1);
  };

  const handleExport = () => {
    const params = {
      format: 'csv',
      ...Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v !== '' && v !== null)
      )
    };
    
    window.open(adminApi.exportAuditLogs(params), '_blank');
    toast.success('Export lancé');
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const CategoryIcon = ({ category }) => {
    const Icon = CATEGORY_ICONS[category] || Database;
    return <Icon className="w-4 h-4" />;
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== '');

  return (
    <div className="space-y-6" data-testid="audit-log-viewer">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-['Cormorant_Garamond'] font-medium text-[#2A312C]">
            Journal des événements
          </h2>
          <p className="text-sm text-[#5C6A61]">
            {total.toLocaleString()} événements enregistrés
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`btn-outline text-sm flex items-center gap-2 ${hasActiveFilters ? 'border-[#A5BBA8] bg-[#A5BBA8]/10' : ''}`}
          >
            <Filter className="w-4 h-4" />
            Filtres
            {hasActiveFilters && (
              <span className="bg-[#A5BBA8] text-white text-xs px-1.5 py-0.5 rounded-full">
                {Object.values(filters).filter(v => v !== '').length}
              </span>
            )}
          </button>
          
          <button
            onClick={loadLogs}
            className="btn-outline text-sm flex items-center gap-2"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          
          <button
            onClick={handleExport}
            className="btn-primary text-sm flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Exporter CSV
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card-default p-4">
            <p className="text-sm text-[#5C6A61]">Total événements</p>
            <p className="text-2xl font-semibold text-[#2A312C]">{stats.total?.toLocaleString() || 0}</p>
          </div>
          <div className="card-default p-4">
            <p className="text-sm text-[#5C6A61]">Erreurs</p>
            <p className="text-2xl font-semibold text-[#C86B51]">{stats.errors || 0}</p>
            <p className="text-xs text-[#5C6A61]">{stats.error_rate || 0}% du total</p>
          </div>
          <div className="card-default p-4">
            <p className="text-sm text-[#5C6A61]">Top catégorie</p>
            <p className="text-lg font-semibold text-[#2A312C]">
              {stats.by_category && Object.keys(stats.by_category).length > 0
                ? CATEGORY_LABELS[Object.keys(stats.by_category)[0]] || Object.keys(stats.by_category)[0]
                : '-'}
            </p>
          </div>
          <div className="card-default p-4">
            <p className="text-sm text-[#5C6A61]">Top action</p>
            <p className="text-lg font-semibold text-[#2A312C]">
              {stats.by_action && Object.keys(stats.by_action).length > 0
                ? ACTION_LABELS[Object.keys(stats.by_action)[0]] || Object.keys(stats.by_action)[0]
                : '-'}
            </p>
          </div>
        </div>
      )}

      {/* Filters Panel */}
      {showFilters && (
        <div className="card-default p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-[#2A312C]">Filtres</h3>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-sm text-[#C86B51] hover:underline flex items-center gap-1"
              >
                <X className="w-3 h-3" />
                Réinitialiser
              </button>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {/* Search */}
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6A61]" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="input-default pl-10"
              />
            </div>
            
            {/* Category */}
            <select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="input-default"
            >
              <option value="">Toutes catégories</option>
              {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
            
            {/* Action */}
            <select
              value={filters.action}
              onChange={(e) => handleFilterChange('action', e.target.value)}
              className="input-default"
            >
              <option value="">Toutes actions</option>
              {Object.entries(ACTION_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
            
            {/* Role */}
            <select
              value={filters.user_role}
              onChange={(e) => handleFilterChange('user_role', e.target.value)}
              className="input-default"
            >
              <option value="">Tous rôles</option>
              {Object.entries(ROLE_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
            
            {/* Success */}
            <select
              value={filters.success}
              onChange={(e) => handleFilterChange('success', e.target.value)}
              className="input-default"
            >
              <option value="">Tous résultats</option>
              <option value="true">Succès</option>
              <option value="false">Erreurs</option>
            </select>
            
            {/* Start Date */}
            <input
              type="date"
              value={filters.start_date}
              onChange={(e) => handleFilterChange('start_date', e.target.value)}
              className="input-default"
              placeholder="Date début"
            />
            
            {/* End Date */}
            <input
              type="date"
              value={filters.end_date}
              onChange={(e) => handleFilterChange('end_date', e.target.value)}
              className="input-default"
              placeholder="Date fin"
            />
          </div>
        </div>
      )}

      {/* Logs Table */}
      <div className="card-default overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#F9F6F0] border-b border-[#E3DED5]">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#5C6A61] uppercase tracking-wider">Date/Heure</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#5C6A61] uppercase tracking-wider">Utilisateur</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#5C6A61] uppercase tracking-wider">Catégorie</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#5C6A61] uppercase tracking-wider">Action</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#5C6A61] uppercase tracking-wider">Description</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-[#5C6A61] uppercase tracking-wider">Résultat</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E3DED5]">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-[#5C6A61]">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Chargement...
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-[#5C6A61]">
                    <Database className="w-8 h-8 mx-auto mb-2 text-[#E3DED5]" />
                    Aucun événement trouvé
                  </td>
                </tr>
              ) : (
                logs.map((log, index) => (
                  <tr 
                    key={log.audit_id || index}
                    className="hover:bg-[#F9F6F0]/50 cursor-pointer transition-colors"
                    onClick={() => setSelectedLog(log)}
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="w-3 h-3 text-[#5C6A61]" />
                        <span className="text-[#2A312C]">{formatTimestamp(log.timestamp)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm">
                        <p className="text-[#2A312C] font-medium">{log.user_name || '-'}</p>
                        <p className="text-[#5C6A61] text-xs">{log.user_email}</p>
                        {log.user_role && (
                          <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${
                            log.user_role === 'admin' ? 'bg-[#C86B51]/10 text-[#C86B51]' :
                            log.user_role === 'employee' ? 'bg-[#A5BBA8]/20 text-[#5C6A61]' :
                            'bg-[#E3DED5] text-[#5C6A61]'
                          }`}>
                            {ROLE_LABELS[log.user_role] || log.user_role}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="p-1.5 rounded-lg bg-[#A5BBA8]/10">
                          <CategoryIcon category={log.category} />
                        </span>
                        <span className="text-sm text-[#2A312C]">
                          {CATEGORY_LABELS[log.category] || log.category}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-sm font-medium ${
                        ['delete', 'cancel', 'refund'].includes(log.action) ? 'text-[#C86B51]' :
                        ['create', 'payment', 'validate', 'checkout'].includes(log.action) ? 'text-[#A5BBA8]' :
                        'text-[#2A312C]'
                      }`}>
                        {ACTION_LABELS[log.action] || log.action}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-[#2A312C] max-w-xs truncate" title={log.description}>
                        {log.description}
                      </p>
                      {log.resource_id && (
                        <p className="text-xs text-[#5C6A61] truncate" title={log.resource_id}>
                          ID: {log.resource_id}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {log.success ? (
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#A5BBA8]/20">
                          <Check className="w-4 h-4 text-[#A5BBA8]" />
                        </span>
                      ) : (
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#C86B51]/20" title={log.error_message}>
                          <AlertTriangle className="w-4 h-4 text-[#C86B51]" />
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#E3DED5]">
            <p className="text-sm text-[#5C6A61]">
              Page {page} sur {totalPages} ({total.toLocaleString()} résultats)
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded-lg hover:bg-[#F9F6F0] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-2 rounded-lg hover:bg-[#F9F6F0] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedLog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedLog(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-[#E3DED5] flex items-center justify-between">
              <h3 className="text-lg font-medium text-[#2A312C]">Détails de l&apos;événement</h3>
              <button onClick={() => setSelectedLog(null)} className="text-[#5C6A61] hover:text-[#2A312C]">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-[#5C6A61] uppercase tracking-wider mb-1">Date/Heure</p>
                  <p className="text-[#2A312C]">{formatTimestamp(selectedLog.timestamp)}</p>
                </div>
                <div>
                  <p className="text-xs text-[#5C6A61] uppercase tracking-wider mb-1">Résultat</p>
                  <p className={selectedLog.success ? 'text-[#A5BBA8]' : 'text-[#C86B51]'}>
                    {selectedLog.success ? 'Succès' : 'Erreur'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-[#5C6A61] uppercase tracking-wider mb-1">Utilisateur</p>
                  <p className="text-[#2A312C]">{selectedLog.user_name || '-'}</p>
                  <p className="text-sm text-[#5C6A61]">{selectedLog.user_email}</p>
                </div>
                <div>
                  <p className="text-xs text-[#5C6A61] uppercase tracking-wider mb-1">Rôle</p>
                  <p className="text-[#2A312C]">{ROLE_LABELS[selectedLog.user_role] || selectedLog.user_role || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-[#5C6A61] uppercase tracking-wider mb-1">Catégorie</p>
                  <p className="text-[#2A312C]">{CATEGORY_LABELS[selectedLog.category] || selectedLog.category}</p>
                </div>
                <div>
                  <p className="text-xs text-[#5C6A61] uppercase tracking-wider mb-1">Action</p>
                  <p className="text-[#2A312C]">{ACTION_LABELS[selectedLog.action] || selectedLog.action}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-[#5C6A61] uppercase tracking-wider mb-1">Description</p>
                  <p className="text-[#2A312C]">{selectedLog.description}</p>
                </div>
                {selectedLog.resource_id && (
                  <div className="col-span-2">
                    <p className="text-xs text-[#5C6A61] uppercase tracking-wider mb-1">ID Ressource</p>
                    <p className="text-[#2A312C] font-mono text-sm">{selectedLog.resource_id}</p>
                  </div>
                )}
                {selectedLog.ip_address && (
                  <div>
                    <p className="text-xs text-[#5C6A61] uppercase tracking-wider mb-1">Adresse IP</p>
                    <p className="text-[#2A312C] font-mono text-sm">{selectedLog.ip_address}</p>
                  </div>
                )}
                {!selectedLog.success && selectedLog.error_message && (
                  <div className="col-span-2">
                    <p className="text-xs text-[#5C6A61] uppercase tracking-wider mb-1">Message d&apos;erreur</p>
                    <p className="text-[#C86B51]">{selectedLog.error_message}</p>
                  </div>
                )}
                {selectedLog.details && Object.keys(selectedLog.details).length > 0 && (
                  <div className="col-span-2">
                    <p className="text-xs text-[#5C6A61] uppercase tracking-wider mb-1">Détails additionnels</p>
                    <pre className="bg-[#F9F6F0] p-3 rounded-lg text-sm text-[#2A312C] overflow-x-auto">
                      {JSON.stringify(selectedLog.details, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
